import { useEffect, useState } from 'react'
import axios from 'axios'
import Container from '@mui/material/Container'
import { Typography, CircularProgress, Select, MenuItem, Stack, Chip } from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { downloadExcel } from '../../../utility/excelDownloadUtils'
import BillingMonthlyTable from './components/BillingMonthlyTable'
import BillingRawValuesTable from './components/BillingRawValuesTable'

export default function Utilization() {
  function daysInMonth(month, year) {
    const daysInMonths = [
      31,
      year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ]
    return daysInMonths[month - 1]
  }

  const { user } = useAuthContext()

  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

  let axiosOptions = {}
  if (user) {
    axiosOptions = {
      headers: { Authorization: `Bearer ${user.token}` },
    }
  }

  const [isLoading, setIsLoading] = useState(false)
  const [emData, setEmData] = useState(null)
  const [devices, setDevices] = useState([])
  const [subOrgs, setSubOrgs] = useState([])
  const [org, setOrg] = useState('')
  const [m, setM] = useState(new Date().getMonth() + 1)
  const [y, setY] = useState(new Date().getFullYear())
  const [d, setD] = useState(-1)
  const [selectedSubOrg, setSelectedSubOrg] = useState('KTPL')
  const [isDailyUtilization, setIsDailyUtilization] = useState(true)
  const [fullDataUtil, setFullDataUtil] = useState([])
  const [fullDataGen, setFullDataGen] = useState([])
  const [grandTotalMonthlyUtilizationUtil, setGrandTotalMonthlyUtilizationUtil] = useState(0)
  const [grandTotalMonthlyUtilizationGen, setGrandTotalMonthlyUtilizationGen] = useState(0)
  const [emRates, setEmRates] = useState({ rate_kwh_util: 1, rate_kwh_gen: 1 })

  const gray = 'rgba(0, 0, 0, 0.08)'

  let years = []
  let days = []

  for (let i = 2024; i <= new Date().getFullYear(); i++) {
    years.push(i)
  }
  for (let i = 1; i <= daysInMonth(m, y); i++) {
    days.push(i)
  }

  useEffect(() => {
    async function getDataFromServerD() {
      setIsLoading(true)

      const org = 'KTPL'
      const config = (await axios.get(`${NODE_BASE_URL}/iot/em/config/${org}`, axiosOptions)).data

      let devices

      if (selectedSubOrg !== 'All') {
        devices = config.deviceMappings
          .filter((dm) => dm.sub_organization === selectedSubOrg)
          .map((dm) => dm.controller_key.trim() + '_' + dm.device_key.trim())
      } else {
        devices = config.deviceMappings.map(
          (dm) => dm.controller_key.trim() + '_' + dm.device_key.trim()
        )
      }
      const subOrgs = config.deviceMappings
        .map((dm) => dm.sub_organization)
        .filter((value, index, array) => array.indexOf(value) === index)

      const emRates = config.emRates.find((r) => r.m === m && r.y === y)

      const payload = {
        devices,
        d,
        m,
        y,
        measurements: ['WH_UTIL', 'WH_GEN'],
      }

      const dataResult = (await axios.post(`${NODE_BASE_URL}/iot/em/d`, payload, axiosOptions)).data

      const u = dataResult.fullDataUtil
      const g = dataResult.fullDataGen

      setDevices(devices)
      setSubOrgs(subOrgs)
      setOrg(org)
      setEmRates(emRates)
      setFullDataUtil(u)
      setFullDataGen(g)
      setIsLoading(false)
    }

    async function getDataFromServerM() {
      setIsLoading(true)

      const org = 'KTPL'
      const config = (await axios.get(`${NODE_BASE_URL}/iot/em/config/${org}`, axiosOptions)).data

      let devices

      if (selectedSubOrg !== 'All') {
        devices = config.deviceMappings
          .filter((dm) => dm.sub_organization === selectedSubOrg)
          .map((dm) => dm.controller_key.trim() + '_' + dm.device_key.trim())
      } else {
        devices = config.deviceMappings.map(
          (dm) => dm.controller_key.trim() + '_' + dm.device_key.trim()
        )
      }
      const subOrgs = config.deviceMappings
        .map((dm) => dm.sub_organization)
        .filter((value, index, array) => array.indexOf(value) === index)

      const emRates = config.emRates.find((r) => r.m === m && r.y === y)

      const payload = {
        devices,
        d,
        m,
        y,
        measurements: ['WH_UTIL', 'WH_GEN'],
      }

      const dataResult = (await axios.post(`${NODE_BASE_URL}/iot/em/m`, payload, axiosOptions)).data

      const u = dataResult.fullDataUtil
      const g = dataResult.fullDataGen

      let grandTotalMonthlyUtilizationUtil = 0
      let grandTotalMonthlyUtilizationGen = 0

      for (let i = 1; i < u[u.length - 1].length; i++) {
        grandTotalMonthlyUtilizationUtil += u[u.length - 1][i]
      }
      for (let i = 1; i < g[g.length - 1].length; i++) {
        grandTotalMonthlyUtilizationGen += g[g.length - 1][i]
      }

      setDevices(devices)
      setSubOrgs(subOrgs)
      setOrg(org)
      setEmRates(emRates)
      setFullDataUtil(u)
      setFullDataGen(g)
      setGrandTotalMonthlyUtilizationUtil(grandTotalMonthlyUtilizationUtil)
      setGrandTotalMonthlyUtilizationGen(grandTotalMonthlyUtilizationGen)
      setIsLoading(false)
    }
    if (user) {
      if (isDailyUtilization) {
        getDataFromServerM()
      } else {
        getDataFromServerD()
      }
    }
  }, [user, selectedSubOrg, m, y, d])

  return (
    <>
      <Container>
        <div
          style={{
            display: 'flex',
            alignItems: 'left',
            justifyContent: 'space-between',
            padding: '0px 8px',
          }}
        >
          <h2>Electricity Meters - Billing (KTPL)</h2>
        </div>
        <Stack
          direction="row"
          spacing={1}
          style={{
            marginBottom: '2rem',
          }}
        >
          <Chip
            label="All"
            variant="outlined"
            onClick={() => setSelectedSubOrg('All')}
            style={{
              backgroundColor:
                selectedSubOrg === 'All' || !subOrgs.includes(selectedSubOrg)
                  ? `${gray}`
                  : 'inherit',
            }}
          />
          {subOrgs &&
            subOrgs.map((subOrg) => (
              <Chip
                label={subOrg}
                variant="outlined"
                onClick={() => setSelectedSubOrg(subOrg)}
                style={{
                  backgroundColor: selectedSubOrg === subOrg ? `${gray}` : 'inherit',
                }}
              />
            ))}
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          style={{
            marginBottom: '2rem',
          }}
        >
          <Chip
            label="Daily Utilization"
            variant="outlined"
            onClick={() => {
              setD(-1)
              setIsDailyUtilization(true)
            }}
            style={{
              backgroundColor: isDailyUtilization ? `${gray}` : 'inherit',
            }}
          />
          <Chip
            label="Raw Values"
            variant="outlined"
            onClick={() => {
              setD(new Date().getDate())
              setIsDailyUtilization(false)
            }}
            style={{ backgroundColor: !isDailyUtilization ? `${gray}` : 'inherit' }}
          />
        </Stack>
        <div>
          {!isDailyUtilization && (
            <Select
              size="small"
              color="primary"
              variant="outlined"
              fullWidth={false}
              value={d}
              onChange={(e) => setD(e.target.value)}
            >
              {days.map((day) => (
                <MenuItem value={day}>{day}</MenuItem>
              ))}
            </Select>
          )}
          <Select
            size="small"
            color="primary"
            variant="outlined"
            fullWidth={false}
            value={m}
            onChange={(e) => setM(e.target.value)}
          >
            <MenuItem key={1} value={1}>
              Jan
            </MenuItem>
            <MenuItem key={2} value={2}>
              Feb
            </MenuItem>
            <MenuItem key={3} value={3}>
              Mar
            </MenuItem>
            <MenuItem key={4} value={4}>
              Apr
            </MenuItem>
            <MenuItem key={5} value={5}>
              May
            </MenuItem>
            <MenuItem key={6} value={6}>
              Jun
            </MenuItem>
            <MenuItem key={7} value={7}>
              Jul
            </MenuItem>
            <MenuItem key={8} value={8}>
              Aug
            </MenuItem>
            <MenuItem key={9} value={9}>
              Sep
            </MenuItem>
            <MenuItem key={10} value={10}>
              Oct
            </MenuItem>
            <MenuItem key={11} value={11}>
              Nov
            </MenuItem>
            <MenuItem key={12} value={12}>
              Dec
            </MenuItem>
          </Select>
          <Select
            size="small"
            color="primary"
            variant="outlined"
            fullWidth={false}
            value={y}
            onChange={(e) => setY(e.target.value)}
          >
            {years.map((year) => (
              <MenuItem value={year}>{year}</MenuItem>
            ))}
          </Select>
        </div>
      </Container>

      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh',
          }}
        >
          <CircularProgress color="primary" size={50} />
        </div>
      ) : isDailyUtilization ? (
        <>
          <BillingMonthlyTable
            isUtil={true}
            fullData={fullDataUtil}
            devices={devices}
            grandTotalMonthlyUtilization={grandTotalMonthlyUtilizationUtil}
            emRate={emRates.rate_kwh_util}
          />
          <br />
          <br />
          <BillingMonthlyTable
            isUtil={false}
            fullData={fullDataGen}
            devices={devices}
            grandTotalMonthlyUtilization={grandTotalMonthlyUtilizationGen}
            emRate={emRates.rate_kwh_gen}
          />
        </>
      ) : (
        <>
          <BillingRawValuesTable
            isUtil={true}
            fullData={fullDataUtil}
            devices={devices}
            m={m}
            y={y}
            d={d}
          />
          <br />
          <br />
          <BillingRawValuesTable
            isUtil={false}
            fullData={fullDataGen}
            devices={devices}
            m={m}
            y={y}
            d={d}
          />
        </>
      )}
    </>
  )
}
